import mpegts from 'mpegts.js'
import { defineComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import type { LotteryType } from '@/stores/region-store'
import { getApiV1LotteryLiveSource } from '@/api/sdk.gen'

function logDebug(...args: unknown[]) {
  console.log('[Lottery][Player]', ...args)
}

/**
 * 从后端动态获取指定彩种的直播 FLV 地址。
 *
 * 接口 /api/v1/lottery-live-source 同时返回港彩(hk)与澳彩(macau)两路地址，
 * 按当前彩种取用；返回空字符串表示该彩种暂无可用源。
 * 后端约定：到规定时间后若播放不出来，需重新请求该接口获取最新地址。
 */
async function fetchStreamUrl(type: LotteryType): Promise<string> {
  try {
    const res = await getApiV1LotteryLiveSource()
    const data = res.data?.data
    if (!data) return ''
    return (type === 'hk' ? data.hk : data.macau) ?? ''
  } catch (e) {
    logDebug('获取直播源失败', type, e)
    return ''
  }
}

/** 流加载失败后的重试间隔（毫秒） */
const RETRY_INTERVAL_MS = 3 * 1000

/** 流连接超时时间：超过此时间未收到 METADATA 则自动重试（毫秒） */
const STREAM_TIMEOUT_MS = 5 * 1000

/**
 * 直播视频播放器（FLV）
 *
 * 使用 mpegts.js 播放 FLV 直播流。
 * 仅在开奖直播期间由父组件条件渲染挂载，挂载即自动播放，卸载时自动销毁。
 * 流加载失败时每 RETRY_INTERVAL_MS 毫秒自动重试。
 */
const LiveStreamPlayer = defineComponent({
  props: {
    lotteryType: { type: String as () => LotteryType, required: true },
  },
  setup(props) {
    const videoEl = ref<HTMLVideoElement | null>(null)
    let player: mpegts.Player | null = null
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let streamTimeoutTimer: ReturnType<typeof setTimeout> | undefined
    const showLoadingMask = ref(true)
    // 创建轮次标记：异步拉取直播源期间若发生卸载/切换/重试，递增此值使旧的异步结果失效
    let playerEpoch = 0

    async function createPlayer(type: LotteryType) {
      if (!videoEl.value) return
      if (!mpegts.isSupported()) {
        logDebug('浏览器不支持 MSE，无法播放 FLV')
        return
      }

      const epoch = ++playerEpoch
      showLoadingMask.value = true

      // 每次创建播放器都向后端重新拉取最新直播源地址
      const url = await fetchStreamUrl(type)
      // 异步拉取期间若已卸载、切换彩种或触发新一轮创建，则放弃本次结果
      if (epoch !== playerEpoch || !videoEl.value) return
      if (!url) {
        logDebug('暂无可用直播源，稍后重试', type)
        scheduleRetry()
        return
      }

      logDebug('创建播放器', type, url)

      player = mpegts.createPlayer({
        type: 'flv',
        url,
        isLive: true,
      })

      player.attachMediaElement(videoEl.value)
      player.load()
      videoEl.value.play().catch(() => {
        // play() 返回 Promise，忽略 AbortError
      })

      // 超时未收到数据则自动重试，防止流挂起导致黑屏
      clearTimeout(streamTimeoutTimer)
      streamTimeoutTimer = setTimeout(() => {
        if (showLoadingMask.value) {
          logDebug('流连接超时，自动重试', type)
          scheduleRetry()
        }
      }, STREAM_TIMEOUT_MS)

      player.on(mpegts.Events.METADATA_ARRIVED, () => {
        logDebug('流加载成功')
        clearTimeout(streamTimeoutTimer)
        showLoadingMask.value = false
      })

      player.on(mpegts.Events.ERROR, (errorType, detail) => {
        logDebug('流加载失败', type, errorType, detail)
        clearTimeout(streamTimeoutTimer)
        scheduleRetry()
      })
    }

    function destroyPlayer() {
      // 递增轮次，使任何在途的异步拉取结果失效
      playerEpoch++
      clearTimeout(retryTimer)
      clearTimeout(streamTimeoutTimer)
      if (player) {
        logDebug('销毁播放器')
        player.destroy()
        player = null
      }
    }

    function scheduleRetry() {
      clearTimeout(retryTimer)
      destroyPlayer()
      retryTimer = setTimeout(() => {
        logDebug('重试加载', props.lotteryType)
        void createPlayer(props.lotteryType)
      }, RETRY_INTERVAL_MS)
    }

    onMounted(() => {
      void createPlayer(props.lotteryType)
    })

    onUnmounted(() => {
      destroyPlayer()
    })

    watch(
      () => props.lotteryType,
      (newType) => {
        logDebug('切换彩种', newType)
        destroyPlayer()
        void createPlayer(newType)
      },
    )

    const recreatePlayer = () => {
      logDebug('手动刷新播放器')
      destroyPlayer()
      void createPlayer(props.lotteryType)
    }

    return () => (
      <>
        <video
          ref={videoEl}
          muted={import.meta.env.DEV}
          style="width:100%;height:100%;object-fit:contain;background:#000"
          autoplay
          playsinline
        />
        {showLoadingMask.value && (
          <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;text-align:center;background:rgba(0,0,0,0.55);color:#fff;z-index:1;padding:12px;flex-direction:column">
            <div style="line-height:1.6">
              <div
                class="message-content"
                style="font-size:14px;line-height:1.5;max-width:420px;color:red"
              >
                {props.lotteryType === 'macau' ? '澳门六合彩' : '香港六合彩'}
                攪珠直播時間為每晚 <b>21:30-21:45</b>（北京時間），敬請查看
              </div>
            </div>
            <button
              class="van-button van-button--default van-button--normal"
              style="font-size:14px;margin-top:20px;padding:5px 8px;color:#ee0000;border:1px solid #ee0000;border-radius:4px;cursor:pointer;background:transparent;height:35px!important"
              onClick={recreatePlayer}
            >
              <div class="van-button__content">
                <span class="van-button__text">点击刷新</span>
              </div>
            </button>
          </div>
        )}
      </>
    )
  },
})

export default LiveStreamPlayer
